import useCustomHook from '@/hooks/useCustomHook';
import { areNumbersAlmostEqual } from '@/modules/math/areNumbersAlmostEqual';
import { useEffect } from 'react';

/** Cues with start and end properties, onCue always gets an array of cues each marked as 'STARTING' or 'ENDING' */
export default function useAdvancedCues({
  currentTime,
  progressInterval,
  cues,
  onCue,
}) {
  const { state, actions } = useCustomHook({
    defaultState: {
      processedCues: [],
    },
  });

  useEffect(() => {
    if (cues && onCue) {
      const processedCues = getProcessedCuesAtCurrentTime(
        currentTime,
        state.processedCues,
        progressInterval
      );

      const detectedCues = findCuesByTime(currentTime, cues, progressInterval);

      const dedupedDetectedCues = detectedCues.filter((cue) => {
        return !processedCues.some(
          (processedCue) =>
            processedCue.id === cue.id && processedCue.status === cue.status
        );
      });

      if (dedupedDetectedCues.length) {
        onCue(dedupedDetectedCues);
      }

      actions.setState({
        processedCues: [...processedCues, ...dedupedDetectedCues],
      });
    }
  }, [cues, currentTime]);
}

function getProcessedCuesAtCurrentTime(
  currentTime,
  processedCues,
  progressInterval
) {
  if (!processedCues.length) return processedCues;
  const lastProcessedCue = processedCues[processedCues.length - 1];
  const lastCueTime =
    lastProcessedCue.status == 'STARTING'
      ? lastProcessedCue.start
      : lastProcessedCue.end;

  if (
    currentTime < lastCueTime &&
    !areNumbersAlmostEqual(
      currentTime * 1000,
      lastCueTime * 1000,
      progressInterval
    )
  ) {
    //means the user seeked the player backwards
    processedCues = processedCues.filter((processedCue) => {
      return (
        (processedCue.status == 'ENDING' && processedCue.end <= currentTime) ||
        (processedCue.status == 'STARTING' && processedCue.start <= currentTime)
      );
    });
  }
  return processedCues;
}

export function findCuesByTime(time, cues, progressInterval) {
  const detectedStartingCues = cues.filter((cue) => {
    const isStarting = areNumbersAlmostEqual(
      cue.start * 1000,
      time * 1000,
      progressInterval
    );
    return isStarting;
  });
  const detectedEndingCues = cues.filter((cue) => {
    const isEnding = areNumbersAlmostEqual(
      cue.end * 1000,
      time * 1000,
      progressInterval
    );
    return isEnding;
  });
  const detectedCues = [
    ...detectedEndingCues.map((cue) => ({ ...cue, status: 'ENDING' })),
    ...detectedStartingCues.map((cue) => ({ ...cue, status: 'STARTING' })),
  ];

  return detectedCues;
}
